<template>
  <EquipmentRow
    :key="`${props.stack || 'default'}-${selectedMle?.id || 'none'}`"
    data-cy="equipment-row"
  >
    <template #left></template>
    <template #default="{ toggle }">
      <AddRemoveButtons
        :hide-add-below="true"
        :disable-remove="!selectedMle?.id"
        data-cy="add-remove-buttons"
        @remove="
          props.stack === 'lower'
            ? equipmentForm.update({
                r_lower_mle: null,
                lower_mle_id: null
              } as Partial<typeof equipmentForm.data.value>)
            : equipmentForm.update({
                r_mle: null,
                mle_id: null
              })
        "
      >
        <template #middle-content>
          <EquipmentPanel
            class="flex-grow-1"
            :rule-names="[
              'motor_lead_cable_length_warning',
              'motor_lead_cable_length_error',
              'mle_temperature',
              'mle_temperature_warning',
              'mle_obsolete',
              'mle_non_lead',
              'motor_max_amps_mle',
              'mle_max_pothead_temperature_check',
              'mle_clearance_traverse',
              'mle_clearance_set'
            ]"
            data-cy="equipment-panel"
            @toggle="toggle"
          >
            <template #header>
              <span data-cy="equipment-panel-header">MLE</span>
            </template>
            <template v-if="selectedMle" #trigger>
              <span data-cy="equipment-panel-trigger">{{
                computeMleLabel(selectedMle)
              }}</span>
            </template>
          </EquipmentPanel>
        </template>
      </AddRemoveButtons>
    </template>
    <template #expand="{ toggle }">
      <MleSearchView
        :is-admin="false"
        data-cy="mle-search-view"
        @select="handleMleSelect($event, toggle)"
      />
    </template>
  </EquipmentRow>
  <!-- MLE inputs below the selection panel -->
  <div class="mle-inputs-container">
    <div class="splitUnits mle-input-row">
      <ConverterInput
        label="Length"
        :model-value="selectedMle?.length_feet || 0"
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        mode="convert"
        data-cy="length-converter-input"
      />
      <ConverterInput
        label="Weight"
        :model-value="mleWeight"
        :base-unit="MassUnits.Pounds"
        :model="MassUnits.options()"
        mode="convert"
      />
    </div>
    <div class="splitUnits mle-input-row">
      <ConverterInput
        v-tooltip.top="
          `Recommended MLE Length targets ${
            recommendedLengthBuffer + ' ' + lengthUnit.label
          } above Pump Discharge`
        "
        label="Recommeneded MLE Length"
        :model-value="recommendedMleLengthFt || 0"
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        mode="convert"
        data-cy="recommended-mle-length-converter-input"
      />
      <ConverterInput
        v-tooltip.top="
          `Minimum MLE Length targets ${
            minimumLengthBuffer + ' ' + lengthUnit.label
          } above Pump Discharge`
        "
        label="Minimum MLE Length"
        :model-value="lengthForMleFt || 0"
        :base-unit="LengthUnits.Feet"
        :model="LargeLengthUnits.options()"
        mode="convert"
        data-cy="minimum-mle-length-converter-input"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import MleSearchView from "./MleSearchView.vue";
import { computeMleLabel } from "@/models/equipment/mle/properties";
import { injectStrict } from "@/utils/utils";
import {
  equipmentFormKey,
  scenarioKey,
  scenarioCalculationsKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import type { ConvertableUnit } from "@/libs/units";
import {
  MLE_ADDITIONAL_LENGTH_FEET,
  MLE_RECOMMENDED_ADDITIONAL_LENGTH_FEET
} from "@/libs/calculations/constants";
import type { Motor_Lead_Extensions } from "gql/api";
import type { StackLocation } from "../d";

const props = defineProps<{
  stack?: StackLocation;
}>();

const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);
const scenarioCalculations = injectStrict(scenarioCalculationsKey);
const lengthForMleFt = computed(
  () => scenarioCalculations.equipmentLengths.lengthForMleFt.value
);
const recommendedMleLengthFt = computed(
  () => scenarioCalculations.equipmentLengths.recommendedMleLengthFt.value
);
const mleWeight = computed(
  () => scenarioCalculations.equipmentWeights.mleWeightLbs.value
);

// Stack-aware MLE selection
const selectedMle = computed(() => {
  if (props.stack === "lower") {
    return equipment.value.r_lower_mle;
  }
  return equipment.value.r_mle;
});

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit } = useScenarioUnits(scenario, sizing);

const lengthUnit = computed<ConvertableUnit>(
  () => getLargeLengthUnit("Recommeneded MLE Length.convert.ft").value
);

const recommendedLengthBuffer = computed(() => {
  return lengthUnit.value
    .toThisUnit(MLE_RECOMMENDED_ADDITIONAL_LENGTH_FEET)
    .toFixed(2);
});

const minimumLengthBuffer = computed(() => {
  return lengthUnit.value.toThisUnit(MLE_ADDITIONAL_LENGTH_FEET).toFixed(2);
});

provide(
  "selected-equipment-id",
  computed(() => selectedMle.value?.id)
);

function handleMleSelect(mle: Motor_Lead_Extensions, toggle: () => void) {
  toggle();
  if (props.stack === "lower") {
    equipmentForm.update({
      r_lower_mle: mle,
      lower_mle_id: mle?.id
    } as Partial<typeof equipmentForm.data.value>);
  } else {
    equipmentForm.update({
      r_mle: mle,
      mle_id: mle?.id
    });
  }
}
</script>

<style lang="scss">
.mle-inputs-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1em;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  align-items: center;
}

.splitUnits {
  display: flex;
  .converter:last-child {
    margin-left: 1em;
  }
}

.mle-input-row {
  display: flex;
  gap: 1em;
  align-items: center;
  // Set a minimum width that will cause wrapping when space is cramped
  min-width: min(100%, 500px);
  flex: 1 1 auto;
}

@media only screen and (max-width: 750px) {
  .splitUnits {
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.5rem;
  }

  .mle-input-row {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}
</style>
